/* CORVIA — per-bottle pricing for the product page.

   Two jobs:

   1. Move Dawn's price block directly above the Kaching bundle widget and
      show the selected tier's per-bottle price there. Dawn's own figure is the
      base variant ($69.00) and does not follow the bundle selection, so it is
      replaced. The compare-at is hidden: it is a bundle total and would read
      as a far bigger discount than it is beside a per-bottle figure.

   2. Add a per-bottle line and a savings badge to each bundle bar, which
      Kaching itself has no setting for. These ARE computed, but only from the
      numbers the app already renders: per-bottle = bar total / bottle count,
      savings = compare-at total - total.

   Nothing the app renders is ever rewritten in place. We add sibling nodes and
   a marker class, and CSS hides the originals — so a re-read always sees the
   app's own numbers, and if any parse fails that bar is simply left alone. The
   theme price is the one thing we do overwrite, and it is always derived from
   the bar (never re-read from itself), so repeated passes cannot drift. */
(function () {
  'use strict';

  var BLOCK = '.kaching-bundles__block';
  var BAR = '.kaching-bundles__bar';

  /* ---------- money ---------- */

  // "$1,234.56" -> { prefix: "$", value: 1234.56, suffix: "" }
  function parseMoney(text) {
    if (!text) return null;
    var m = String(text).replace(/\s+/g, ' ').match(/^\s*(\D*?)\s*([\d.,]+)\s*(\D*?)\s*$/);
    if (!m) return null;
    var digits = m[2];
    // A comma decimal separator (1.234,56) would need different formatting on
    // the way out; rather than risk printing a wrong number, decline the bar.
    if (/,\d{1,2}$/.test(digits)) return null;
    var value = parseFloat(digits.replace(/,/g, ''));
    if (!isFinite(value)) return null;
    return { prefix: m[1] || '', suffix: m[3] || '', value: value };
  }

  function money(sample, value) {
    var parts = value.toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return sample.prefix + parts.join('.') + sample.suffix;
  }

  // "180-day supply (6 Bottles)" -> 6
  function unitCount(text) {
    var m = String(text || '').match(/(\d+)\s*bottles?\b/i);
    return m ? parseInt(m[1], 10) : 0;
  }

  /* ---------- bundle bars ---------- */

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  /* Writes the per-bottle / total / savings stack into a bar's price column.
     Only touches the DOM when a value actually changed, so our own writes
     cannot feed the mutation observer back into a loop. */
  function renderPriceBox(pricing, unit, total, save) {
    var box = pricing.querySelector('.cvk-price');
    if (!box) {
      box = el('div', 'cvk-price');
      box.appendChild(el('span', 'cvk-save'));
      var u = el('span', 'cvk-unit');
      u.appendChild(el('b', 'cvk-unit-amount'));
      u.appendChild(el('i', 'cvk-unit-label', '/bottle'));
      box.appendChild(u);
      box.appendChild(el('span', 'cvk-total'));
      pricing.appendChild(box);
    }
    var amount = box.querySelector('.cvk-unit-amount');
    var saveEl = box.querySelector('.cvk-save');
    var totalEl = box.querySelector('.cvk-total');
    if (amount.textContent !== unit) amount.textContent = unit;
    if (saveEl.textContent !== save) saveEl.textContent = save;
    if (totalEl.textContent !== total) totalEl.textContent = total;
    saveEl.hidden = !save;
    totalEl.hidden = !total;
  }

  function decorateBar(bar) {
    var pricing = bar.querySelector('.kaching-bundles__bar-pricing');
    var priceEl = bar.querySelector('.kaching-bundles__bar-price');
    var subEl = bar.querySelector('.kaching-bundles__bar-subtitle');
    if (!pricing || !priceEl || !subEl) return;

    var qty = unitCount(subEl.textContent);
    var price = parseMoney(priceEl.textContent);
    if (!qty || !price) return;

    var fullEl = bar.querySelector('.kaching-bundles__bar-full-price');
    var full = fullEl ? parseMoney(fullEl.textContent) : null;

    var unit = money(price, price.value / qty);
    var total = qty > 1 ? money(price, price.value) + ' total' : '';
    var save = full && full.value > price.value
      ? 'Save ' + money(price, full.value - price.value)
      : '';

    renderPriceBox(pricing, unit, total, save);

    /* The merchant has put the per-bottle figure in Kaching's label slot, which
       now duplicates the price we render. Hide it only while it really is a
       duplicate — if the label is later changed to "Most Popular" or anything
       else, it comes straight back. */
    var label = bar.querySelector('.kaching-bundles__bar-label');
    if (label) {
      var squash = function (t) { return String(t).replace(/\s+/g, '').toLowerCase(); };
      var dup = squash(label.textContent) === squash(unit + '/bottle');
      if (label.hidden !== dup) label.hidden = dup;
    }

    bar.classList.add('cvk-bar');
  }

  /* The Subscribe & Save row prices whichever bundle is selected, so its
     bottle count comes from the selected bar rather than from a subtitle of
     its own. No savings pill here — "Subscribe & Save 15%" already says it. */
  function decorateSubscription() {
    var card = document.querySelector('.kaching-bundles__subscriptions ' + BAR);
    if (!card) return;
    var pricing = card.querySelector('.kaching-bundles__bar-pricing');
    var priceEl = card.querySelector('.kaching-bundles__bar-price');
    if (!pricing || !priceEl) return;

    var subtitle = document.querySelector('.kaching-bundles__bar--selected .kaching-bundles__bar-subtitle');
    var qty = subtitle ? unitCount(subtitle.textContent) : 0;
    var price = parseMoney(priceEl.textContent);
    if (!qty || !price) return;

    renderPriceBox(
      pricing,
      money(price, price.value / qty),
      qty > 1 ? money(price, price.value) + ' total' : '',
      ''
    );
    card.classList.add('cvk-bar');
  }

  function decorateBars() {
    var bars = document.querySelectorAll(BAR);
    for (var i = 0; i < bars.length; i++) decorateBar(bars[i]);
    decorateSubscription();
  }

  /* ---------- theme price ---------- */

  function themePriceWrap() {
    var scope = document.querySelector('.product__info-container');
    return scope && scope.querySelector('[id^="price-"]');
  }

  /* Sit the price directly above the bundle widget. Kaching injects itself
     inside Dawn's product form, so this has to be measured off the widget
     rather than off any fixed spot in the template. */
  function positionPrice() {
    var wrap = themePriceWrap();
    var block = document.querySelector(BLOCK);
    if (!wrap || !block || wrap.contains(block)) return;
    if (wrap.nextElementSibling === block) return;
    block.parentNode.insertBefore(wrap, block);
  }

  /* The per-bottle figure for whichever tier is selected, read straight off
     the bar we decorated. Returns null when we could not work it out, in which
     case Dawn's own price is left exactly as the theme rendered it. */
  function selectedUnitPrice() {
    var bar = document.querySelector('.kaching-bundles__bar--selected.cvk-bar');
    var amount = bar && bar.querySelector('.cvk-unit-amount');
    var text = amount && amount.textContent;
    return text ? text : null;
  }

  /* "MOST POPULAR selected" — the title of whichever bar is active, so the
     shopper can see which tier the headline price belongs to. */
  function writeSelectedTag(price) {
    var title = document.querySelector('.kaching-bundles__bar--selected .kaching-bundles__bar-title');
    var text = title ? title.textContent.trim() : '';
    var tag = price.querySelector('.cvk-picked');
    if (!text) {
      if (tag) tag.hidden = true;
      return;
    }
    if (!tag) {
      tag = el('span', 'cvk-picked');
      price.appendChild(tag);
    }
    var label = text + ' selected';
    if (tag.textContent !== label) tag.textContent = label;
    tag.hidden = false;
  }

  function writeThemePrice() {
    var wrap = themePriceWrap();
    if (!wrap) return;
    var price = wrap.querySelector('.price');
    if (!price) return;
    price.classList.add('cvk-theme-price');

    var unit = selectedUnitPrice();
    if (!unit) return;

    writeSelectedTag(price);

    // Dawn rebuilds this markup on every variant change, so rebuild each pass.
    var items = price.querySelectorAll('.price-item--regular, .price-item--sale');
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (item.tagName === 'S') continue; // the compare-at, hidden by CSS
      var amount = item.querySelector('.cvk-amount');
      if (!amount) {
        item.textContent = '';
        amount = el('span', 'cvk-amount');
        item.appendChild(amount);
        item.appendChild(el('i', 'cvk-per', '/bottle'));
      }
      if (amount.textContent !== unit) amount.textContent = unit;
    }
  }

  /* ---------- CTA scrolling ----------

     The section CTAs are authored as href="#MainContent" so they still do
     something sensible without JS. The bundle widget is injected by the app and
     has no stable id to link to, so the jump is taken over here and aimed at
     the widget itself — landing on the price, which sits directly above it, so
     the shopper sees the per-bottle figure and the tiers together.

     An explicit Button link set in the theme editor is left alone. */

  function scrollTarget() {
    var block = document.querySelector(BLOCK);
    if (block) {
      var wrap = themePriceWrap();
      return wrap && wrap.nextElementSibling === block ? wrap : block;
    }
    // widget absent (app disabled): the buy button is the next best thing
    return document.querySelector('.product-form__buttons') || document.getElementById('MainContent');
  }

  function stickyOffset() {
    var header = document.querySelector('.shopify-section-header-sticky') ||
                 document.querySelector('.header-wrapper');
    var h = header ? header.getBoundingClientRect().height : 0;
    return Math.round(h) + 18;
  }

  function onCtaClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey) return;
    var el = event.target;
    if (!el || !el.closest) return;
    var link = el.closest('.cvp a[href="#MainContent"]');
    if (!link) return;

    var target = scrollTarget();
    if (!target) return;

    event.preventDefault();
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var top = target.getBoundingClientRect().top + window.pageYOffset - stickyOffset();
    window.scrollTo({ top: top > 0 ? top : 0, behavior: reduce ? 'auto' : 'smooth' });
  }

  /* ---------- run ---------- */

  var queued = false;

  function run() {
    queued = false;
    try {
      positionPrice();
      decorateBars();
      writeThemePrice();
    } catch (e) {
      /* never break the page over a price label */
    }
  }

  function schedule() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(run);
  }

  function boot() {
    run();
    document.addEventListener('click', schedule, true);
    document.addEventListener('click', onCtaClick, false);
    document.addEventListener('change', schedule, true);
    new MutationObserver(schedule).observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  /* Diagnostics: run cv2BundlePrice.report() in the console. */
  window.cv2BundlePrice = {
    refresh: run,
    report: function () {
      var out = { bars: [] };
      var bars = document.querySelectorAll(BAR);
      for (var i = 0; i < bars.length; i++) {
        var b = bars[i];
        var t = function (s) {
          var n = b.querySelector(s);
          return n ? n.textContent.trim() : null;
        };
        out.bars.push({
          title: t('.kaching-bundles__bar-title'),
          subtitle: t('.kaching-bundles__bar-subtitle'),
          appPrice: t('.kaching-bundles__bar-price'),
          appCompare: t('.kaching-bundles__bar-full-price'),
          qty: unitCount(t('.kaching-bundles__bar-subtitle')),
          ourUnit: t('.cvk-unit-amount'),
          ourSave: t('.cvk-save'),
          decorated: b.classList.contains('cvk-bar')
        });
      }
      var wrap = themePriceWrap();
      out.themePriceFound = !!wrap;
      out.themePriceText = wrap ? wrap.textContent.replace(/\s+/g, ' ').trim().slice(0, 90) : null;
      out.aboveBundle = !!(wrap && wrap.nextElementSibling === document.querySelector(BLOCK));
      /* eslint-disable no-console */
      console.log(JSON.stringify(out, null, 2));
      return out;
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
